import type { ActivityDefinition, CodeableConcept, Coding, Extension } from "@medplum/fhirtypes";
import { UCUM_SYSTEM } from "@repo/fhir-systems";
import type {
  ActivityDefinitionSchedulingParams,
  ActivityParameterInput,
  NoteSchemaInput,
  ObservationSchemaInput,
  ProcedureFieldInput,
  ResourceRequirementInput,
  SoapHint,
} from "../schemas/activity-definition.schemas";
import {
  ACTION_PARAMETER_VALUE_URL,
  ACTIVITY_DEFINITION_CATALOG_SYSTEM,
  ACTIVITY_PARAMETER_URL,
  type ActivityDefinitionCatalog,
  CATALOG_DISPLAY_MAP,
  NOTE_SCHEMA_URL,
  OBSERVATION_SCHEMA_URL,
  PROCEDURE_NOTE_CONFIG_URL,
  REQUIRED_RESOURCE_URL,
  SCHEDULING_PARAMS_URL,
  SOAP_HINT_URL,
  SUPPORTS_CONDITIONS_URL,
  TASK_ELEMENTS_URL,
  TASK_PARAMETER_VALUE_URL,
  TREATMENT_CATEGORY_URL,
} from "./constants";

export {
  ACTION_PARAMETER_VALUE_URL,
  ACTIVITY_DEFINITION_CATALOG_SYSTEM,
  ACTIVITY_PARAMETER_URL,
  type ActivityDefinitionCatalog,
  CATALOG_DISPLAY_MAP,
  CATALOG_KIND_MAP,
  NOTE_SCHEMA_URL,
  OBSERVATION_SCHEMA_URL,
  PROCEDURE_NOTE_CONFIG_URL,
  REQUIRED_RESOURCE_URL,
  SCHEDULING_PARAMS_URL,
  SOAP_HINT_URL,
  SUPPORTS_CONDITIONS_URL,
  TASK_ELEMENTS_URL,
  TASK_PARAMETER_VALUE_URL,
  TREATMENT_CATEGORY_URL,
} from "./constants";

/**
 * Build the Medplum `task-elements` extension wiring `Task.owner` to the
 * `%practitioner` parameter passed to PlanDefinition/$apply (i.e. the
 * Practitioner who booked the visit).
 */
export function buildTaskElementsOwnerExtension(): Extension {
  return {
    url: TASK_ELEMENTS_URL,
    extension: [
      {
        url: "owner",
        valueExpression: { language: "text/fhirpath", expression: "%practitioner" },
      },
    ],
  };
}

/** Build a Coding for use in `meta.tag` */
export function catalogTag(catalog: ActivityDefinitionCatalog): Coding {
  return {
    system: ACTIVITY_DEFINITION_CATALOG_SYSTEM,
    code: catalog,
    display: CATALOG_DISPLAY_MAP[catalog],
  };
}

/** Build a CodeableConcept for use in `topic` */
export function catalogTopic(catalog: ActivityDefinitionCatalog): CodeableConcept {
  return {
    coding: [
      {
        system: ACTIVITY_DEFINITION_CATALOG_SYSTEM,
        code: catalog,
        display: CATALOG_DISPLAY_MAP[catalog],
      },
    ],
  };
}

/** Extract catalog from a `meta.tag` array */
export function getCatalogFromTags(tags: Coding[] | undefined): ActivityDefinitionCatalog | undefined {
  const tag = tags?.find((t) => t.system === ACTIVITY_DEFINITION_CATALOG_SYSTEM);
  return tag?.code as ActivityDefinitionCatalog | undefined;
}

/** Build a category extension */
export function buildCategoryExtension(category: string): Extension {
  return { url: TREATMENT_CATEGORY_URL, valueString: category };
}

/** Parse category from extensions array */
export function parseCategoryExtension(extensions: Extension[] | undefined): string | undefined {
  return extensions?.find((e) => e.url === TREATMENT_CATEGORY_URL)?.valueString;
}

/** Build parameter extensions from input array */
export function buildParameterExtensions(parameters: ActivityParameterInput[]): Extension[] {
  return parameters.map((param) => {
    const subExtensions: Extension[] = [
      { url: "name", valueString: param.name },
      { url: "text", valueString: param.text },
      { url: "type", valueCode: param.type },
    ];

    if (param.type === "choice" && param.options?.length) {
      for (const opt of param.options) {
        subExtensions.push({
          url: "option",
          valueCoding: { code: opt.code, display: opt.display },
        });
      }
    }

    if (param.min != null) subExtensions.push({ url: "min", valueDecimal: param.min });
    if (param.max != null) subExtensions.push({ url: "max", valueDecimal: param.max });
    if (param.step != null) subExtensions.push({ url: "step", valueDecimal: param.step });

    return { url: ACTIVITY_PARAMETER_URL, extension: subExtensions };
  });
}

/** Parse parameter extensions back to input array */
export function parseParameterExtensions(extensions: Extension[] | undefined): ActivityParameterInput[] {
  if (!extensions) return [];
  return extensions
    .filter((e) => e.url === ACTIVITY_PARAMETER_URL)
    .map((ext) => {
      const find = (url: string) => ext.extension?.find((e) => e.url === url);
      const result: ActivityParameterInput = {
        name: find("name")?.valueString ?? "",
        text: find("text")?.valueString ?? "",
        type: (find("type")?.valueCode ?? "text") as ActivityParameterInput["type"],
      };
      const options = ext.extension?.filter((e) => e.url === "option");
      if (options?.length) {
        result.options = options.map((o) => ({
          code: o.valueCoding?.code ?? "",
          display: o.valueCoding?.display ?? "",
        }));
      }
      const min = find("min")?.valueDecimal;
      if (min != null) result.min = min;
      const max = find("max")?.valueDecimal;
      if (max != null) result.max = max;
      const step = find("step")?.valueDecimal;
      if (step != null) result.step = step;
      return result;
    });
}

function toFhirDuration(value: number, unit: string) {
  return { value, unit, system: UCUM_SYSTEM, code: unit };
}

/** Build SchedulingParameters FHIR extension from domain params */
export function buildSchedulingParamsExtension(params: ActivityDefinitionSchedulingParams): Extension {
  const subExtensions: Extension[] = [];

  subExtensions.push({
    url: "duration",
    valueDuration: toFhirDuration(params.duration.value, params.duration.unit),
  });

  if (params.bufferBefore) {
    subExtensions.push({
      url: "bufferBefore",
      valueDuration: toFhirDuration(params.bufferBefore.value, params.bufferBefore.unit),
    });
  }
  if (params.bufferAfter) {
    subExtensions.push({
      url: "bufferAfter",
      valueDuration: toFhirDuration(params.bufferAfter.value, params.bufferAfter.unit),
    });
  }
  if (params.bookingAdvanceLimit) {
    subExtensions.push({
      url: "bookingAdvanceLimit",
      valueDuration: toFhirDuration(params.bookingAdvanceLimit.value, params.bookingAdvanceLimit.unit),
    });
  }
  if (params.bookingLimit?.length) {
    for (const limit of params.bookingLimit) {
      subExtensions.push({
        url: "bookingLimit",
        extension: [
          { url: "maxPerPeriod", valueInteger: limit.maxPerPeriod },
          { url: "periodUnit", valueCode: limit.periodUnit },
        ],
      });
    }
  }

  return { url: SCHEDULING_PARAMS_URL, extension: subExtensions };
}

/** A selected parameter value for a protocol action */
export interface ActionParameterValue {
  name: string;
  /** Human-readable label resolved from ActivityDefinition parameter metadata */
  label?: string;
  code: string;
  display: string;
}

/** Build extensions for selected action parameter values */
export function buildActionParameterValueExtensions(values: ActionParameterValue[]): Extension[] {
  return values.map((v) => ({
    url: ACTION_PARAMETER_VALUE_URL,
    extension: [
      { url: "name", valueString: v.name },
      { url: "value", valueCoding: { code: v.code, display: v.display } },
    ],
  }));
}

/** Parse action parameter values from extensions array */
export function parseActionParameterValues(extensions: Extension[] | undefined): ActionParameterValue[] {
  if (!extensions) return [];
  return extensions
    .filter((e) => e.url === ACTION_PARAMETER_VALUE_URL)
    .map((ext) => ({
      name: ext.extension?.find((e) => e.url === "name")?.valueString ?? "",
      code: ext.extension?.find((e) => e.url === "value")?.valueCoding?.code ?? "",
      display: ext.extension?.find((e) => e.url === "value")?.valueCoding?.display ?? "",
    }))
    .filter((v) => v.name && v.code);
}

/** Build extensions for task-level parameter values (same shape, different URL) */
export function buildTaskParameterValueExtensions(values: ActionParameterValue[]): Extension[] {
  return values.map((v) => ({
    url: TASK_PARAMETER_VALUE_URL,
    extension: [
      { url: "name", valueString: v.name },
      { url: "value", valueCoding: { code: v.code, display: v.display } },
    ],
  }));
}

/** Parse task parameter values from extensions array */
export function parseTaskParameterValues(extensions: Extension[] | undefined): ActionParameterValue[] {
  if (!extensions) return [];
  return extensions
    .filter((e) => e.url === TASK_PARAMETER_VALUE_URL)
    .map((ext) => ({
      name: ext.extension?.find((e) => e.url === "name")?.valueString ?? "",
      code: ext.extension?.find((e) => e.url === "value")?.valueCoding?.code ?? "",
      display: ext.extension?.find((e) => e.url === "value")?.valueCoding?.display ?? "",
    }))
    .filter((v) => v.name && v.code);
}

/** Parse SchedulingParameters from a FHIR extensions array. Returns null if not found. */
export function parseSchedulingParamsExtension(
  extensions: Extension[] | undefined,
): ActivityDefinitionSchedulingParams | null {
  const paramExt = extensions?.find((e) => e.url === SCHEDULING_PARAMS_URL);
  if (!paramExt?.extension) return null;

  const findExt = (url: string) => paramExt.extension?.find((e) => e.url === url);

  const parseDuration = (url: string) => {
    const ext = findExt(url);
    const val = ext?.valueDuration;
    if (!val?.value) return undefined;
    return { value: val.value, unit: (val.code ?? val.unit ?? "min") as "min" | "h" };
  };

  const duration = parseDuration("duration");
  if (!duration) return null;

  const bookingLimits = (paramExt.extension?.filter((e) => e.url === "bookingLimit") ?? []).map((ext) => ({
    maxPerPeriod: ext.extension?.find((e) => e.url === "maxPerPeriod")?.valueInteger ?? 0,
    periodUnit: (ext.extension?.find((e) => e.url === "periodUnit")?.valueCode ?? "d") as "d" | "wk",
  }));

  const advanceExt = findExt("bookingAdvanceLimit");
  const advanceVal = advanceExt?.valueDuration;

  return {
    duration: { value: duration.value, unit: duration.unit },
    bufferBefore: parseDuration("bufferBefore"),
    bufferAfter: parseDuration("bufferAfter"),
    bookingAdvanceLimit: advanceVal?.value
      ? { value: advanceVal.value, unit: (advanceVal.code ?? advanceVal.unit ?? "d") as "d" | "wk" }
      : undefined,
    bookingLimit: bookingLimits.length > 0 ? bookingLimits : undefined,
  };
}

type ADParticipant = NonNullable<ActivityDefinition["participant"]>[number];

/** Parse practitioner participant role (CodeableConcept) from AD.participant. */
export function parseParticipantRole(participants: ADParticipant[] | undefined): CodeableConcept | undefined {
  return participants?.find((p) => p.type === "practitioner")?.role;
}

/** Build AD.participant array with a single practitioner role entry. */
export function buildParticipantEntries(role: CodeableConcept): ADParticipant[] {
  return [{ type: "practitioner", role }];
}

function buildFieldExtension(field: ProcedureFieldInput): Extension {
  const subs: Extension[] = [
    { url: "key", valueString: field.key },
    { url: "label", valueString: field.label },
    { url: "type", valueCode: field.type },
  ];
  if (field.unit) subs.push({ url: "unit", valueString: field.unit });
  if (field.min != null) subs.push({ url: "min", valueDecimal: field.min });
  if (field.max != null) subs.push({ url: "max", valueDecimal: field.max });
  if (field.step != null) subs.push({ url: "step", valueDecimal: field.step });
  if (field.helpText) subs.push({ url: "helpText", valueString: field.helpText });
  if (field.options?.length) {
    for (const opt of field.options) {
      subs.push({ url: "option", valueCoding: { code: opt.value, display: opt.label } });
    }
  }
  return { url: "field", extension: subs };
}

const FIELD_TYPE_MIGRATION: Record<string, string> = {
  quantity: "number",
  string: "text",
  "codeable-concept": "choice",
};

function parseFieldExtension(ext: Extension): ProcedureFieldInput {
  const find = (url: string) => ext.extension?.find((e) => e.url === url);
  const options = ext.extension
    ?.filter((e) => e.url === "option")
    .map((e) => ({
      value: e.valueCoding?.code ?? "",
      label: e.valueCoding?.display ?? "",
    }))
    .filter((o) => o.value);
  const rawType = find("type")?.valueCode ?? "text";
  return {
    key: find("key")?.valueString ?? "",
    label: find("label")?.valueString ?? "",
    type: (FIELD_TYPE_MIGRATION[rawType] ?? rawType) as ProcedureFieldInput["type"],
    unit: find("unit")?.valueString,
    min: find("min")?.valueDecimal,
    max: find("max")?.valueDecimal,
    step: find("step")?.valueDecimal,
    helpText: find("helpText")?.valueString,
    ...(options?.length ? { options } : {}),
  };
}

export function buildObservationSchemaExtensions(observations: ObservationSchemaInput[]): Extension[] {
  return observations.map((obs) => {
    const subs: Extension[] = [
      { url: "code", valueCode: obs.code },
      { url: "display", valueString: obs.display },
      { url: "system", valueUri: obs.system },
      { url: "multiComponent", valueBoolean: obs.multiComponent },
    ];
    if (obs.loinc) {
      subs.push({ url: "loinc", valueCode: obs.loinc.code });
      subs.push({ url: "loincDisplay", valueString: obs.loinc.display });
    }
    if (!obs.multiComponent && obs.field) {
      subs.push(buildFieldExtension(obs.field));
    }
    if (obs.multiComponent && obs.components?.length) {
      for (const comp of obs.components) {
        subs.push(buildFieldExtension(comp));
      }
    }
    return { url: OBSERVATION_SCHEMA_URL, extension: subs };
  });
}

export function parseObservationSchema(extensions: Extension[] | undefined): ObservationSchemaInput[] {
  if (!extensions) return [];
  return extensions
    .filter((e) => e.url === OBSERVATION_SCHEMA_URL)
    .map((ext) => {
      const find = (url: string) => ext.extension?.find((e) => e.url === url);
      const multiComponent = find("multiComponent")?.valueBoolean ?? false;
      const fieldExts = ext.extension?.filter((e) => e.url === "field").map(parseFieldExtension) ?? [];

      const loincCode = find("loinc")?.valueCode;
      const loincDisplay = find("loincDisplay")?.valueString;
      return {
        code: find("code")?.valueCode ?? "",
        display: find("display")?.valueString ?? "",
        system: find("system")?.valueUri ?? "",
        multiComponent,
        ...(!multiComponent && fieldExts.length > 0 ? { field: fieldExts[0] } : {}),
        ...(multiComponent && fieldExts.length > 0 ? { components: fieldExts } : {}),
        ...(loincCode ? { loinc: { code: loincCode, display: loincDisplay ?? loincCode } } : {}),
      };
    });
}

export function buildNoteSchemaExtensions(notes: NoteSchemaInput[]): Extension[] {
  return notes.map((note) => {
    const subs: Extension[] = [
      { url: "code", valueCode: note.code },
      { url: "label", valueString: note.label },
    ];
    if (note.placeholder) {
      subs.push({ url: "placeholder", valueString: note.placeholder });
    }
    return { url: NOTE_SCHEMA_URL, extension: subs };
  });
}

export function parseNoteSchema(extensions: Extension[] | undefined): NoteSchemaInput[] {
  if (!extensions) return [];
  return extensions
    .filter((e) => e.url === NOTE_SCHEMA_URL)
    .map((ext) => {
      const find = (url: string) => ext.extension?.find((e) => e.url === url);
      return {
        code: find("code")?.valueCode ?? "",
        label: find("label")?.valueString ?? "",
        placeholder: find("placeholder")?.valueString,
      };
    });
}

export function buildSoapHintExtension(hint: SoapHint): Extension {
  return { url: SOAP_HINT_URL, valueCode: hint };
}

export function parseSoapHint(extensions: Extension[] | undefined): SoapHint | undefined {
  const ext = extensions?.find((e) => e.url === SOAP_HINT_URL);
  return ext?.valueCode as SoapHint | undefined;
}

export function buildSupportsConditionsExtension(value: boolean): Extension {
  return { url: SUPPORTS_CONDITIONS_URL, valueBoolean: value };
}

export function parseSupportsConditions(extensions: Extension[] | undefined): boolean | undefined {
  return extensions?.find((e) => e.url === SUPPORTS_CONDITIONS_URL)?.valueBoolean;
}

/** Build a single extension for the optional procedure note field */
export function buildProcedureNoteConfigExtension(field: ProcedureFieldInput): Extension {
  return { url: PROCEDURE_NOTE_CONFIG_URL, extension: [buildFieldExtension(field)] };
}

/** Parse the optional procedure note field config from extensions */
export function parseProcedureNoteConfig(extensions: Extension[] | undefined): ProcedureFieldInput | undefined {
  const ext = extensions?.find((e) => e.url === PROCEDURE_NOTE_CONFIG_URL);
  const fieldExt = ext?.extension?.find((e) => e.url === "field");
  return fieldExt ? parseFieldExtension(fieldExt) : undefined;
}

/** Build extensions for resource requirements */
export function buildResourceRequirementExtensions(requirements: ResourceRequirementInput[]): Extension[] {
  return requirements.map((req) => {
    const subs: Extension[] = [{ url: "resourceType", valueCode: req.resourceType }];
    if (req.reference) subs.push({ url: "reference", valueString: req.reference });
    if (req.referenceDisplay) subs.push({ url: "referenceDisplay", valueString: req.referenceDisplay });
    if (req.category) {
      subs.push({
        url: "category",
        valueCodeableConcept: req.category,
      });
    }
    subs.push({ url: "quantity", valueInteger: req.quantity });
    return { url: REQUIRED_RESOURCE_URL, extension: subs };
  });
}

/** Parse resource requirements from extensions array */
export function parseResourceRequirements(extensions: Extension[] | undefined): ResourceRequirementInput[] {
  if (!extensions) return [];
  return extensions
    .filter((e) => e.url === REQUIRED_RESOURCE_URL)
    .map((ext) => {
      const find = (url: string) => ext.extension?.find((e) => e.url === url);
      const result: ResourceRequirementInput = {
        resourceType: (find("resourceType")?.valueCode ?? "Location") as "Location" | "Device",
        quantity: find("quantity")?.valueInteger ?? 1,
      };
      const ref = find("reference")?.valueString;
      if (ref) result.reference = ref;
      const refDisplay = find("referenceDisplay")?.valueString;
      if (refDisplay) result.referenceDisplay = refDisplay;
      const cat = find("category")?.valueCodeableConcept;
      if (cat) result.category = cat;
      return result;
    });
}
